import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { fetchVocabularyGroups } from "./vocabularyGroupService";
import { useToast } from "../../../../common/toast/ToastContext";
import Navbar from "../../nav_bar/Navbar";
import Footer from "../../footer/Footer";
import "./VocabularyGroupsPage.css";

const VocabularyGroupsPage = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [hasMore, setHasMore] = useState(true);

  const size = 20;

  useEffect(() => {
    loadGroups(true);
  }, [search]);

  const loadGroups = async (reset = false) => {
    if (loading) return;
    
    setLoading(true);
    const currentPage = reset ? 0 : page;
    const params = { page: currentPage, size };
    if (search.trim()) params.search = search.trim();

    const result = await fetchVocabularyGroups(params);
    if (result.status === 200) {
      // Handle both possible response structures
      const responseData = result.data;
      let newGroups, totalPages, totalItems;
      
      if (responseData.status && responseData.data) {
        // Structure: { status: true, data: [...], totalPages: x, totalItems: y }
        newGroups = responseData.data || [];
        totalPages = responseData.totalPages || 0;
        totalItems = responseData.totalItems || 0;
      } else if (responseData.content) {
        // Spring Boot Page structure: { content: [...], totalPages: x, totalElements: y }
        newGroups = responseData.content || [];
        totalPages = responseData.totalPages || 0;
        totalItems = responseData.totalElements || 0;
      } else {
        // Direct array or other structure
        newGroups = Array.isArray(responseData) ? responseData : [];
        totalPages = 1;
        totalItems = newGroups.length;
      }
      
      setGroups(reset ? newGroups : [...groups, ...newGroups]);
      setTotalItems(totalItems);
      setPage(reset ? 1 : currentPage + 1);
      setHasMore(currentPage + 1 < totalPages);
    } else {
      showToast(result.message || "Failed to load groups", "error");
    }
    setLoading(false);
  };

  const handleSearch = (e) => {
    setSearch(e.target.value);
    setPage(0);
    setGroups([]);
    setHasMore(true);
  };

  const handleGroupClick = (groupId) => {
    navigate(`/study/vocabulary/groups/${groupId}`);
  };

  const loadMore = () => {
    if (hasMore && !loading) {
      loadGroups();
    }
  };

  const truncateText = (text, maxLength = 100) => {
    if (!text) return "";
    return text.length > maxLength ? text.substring(0, maxLength) + "..." : text;
  };

  return (
    <>
      <Navbar />
      <div className="vocabulary-groups-page">
        <div className="container">
          <div className="header">
            <h1>Vocabulary Groups</h1>
            <p>Explore curated collections of vocabulary words</p>
          </div>

        {/* Search */}
        <div className="search-section">
          <div className="search-box">
            <input
              type="text"
              placeholder="Search vocabulary groups..."
              value={search}
              onChange={handleSearch}
              className="search-input"
            />
            <i className="search-icon">🔍</i>
          </div>
        </div>

        {/* Results Info */}
        {totalItems > 0 && (
          <div className="results-info">
            Found {totalItems} group{totalItems !== 1 ? 's' : ''}
          </div>
        )}

        {/* Groups Grid */}
        <div className="groups-grid">
          {groups.map((group) => (
            <div
              key={group.id}
              className="group-card"
              onClick={() => handleGroupClick(group.id)}
            >
              <div className="group-header">
                <h3 className="group-name">{group.name}</h3>
                <span className="word-count-badge">
                  {group.wordCount} word{group.wordCount !== 1 ? 's' : ''}
                </span>
              </div>
              
              {group.description && (
                <p className="group-description">
                  {truncateText(group.description)}
                </p>
              )}
              
              <div className="group-footer">
                <span className="view-link">View Group →</span>
              </div>
            </div>
          ))}
        </div>

        {/* Loading */}
        {loading && (
          <div className="loading-section">
            <div className="loading-spinner"></div>
            <p>Loading groups...</p>
          </div>
        )}

        {/* Load More */}
        {!loading && hasMore && groups.length > 0 && (
          <div className="load-more-section">
            <button onClick={loadMore} className="load-more-btn">
              Load More Groups
            </button>
          </div>
        )}

        {/* No Results */}
        {!loading && groups.length === 0 && (
          <div className="no-results">
            <div className="no-results-icon">📚</div>
            <h3>No vocabulary groups found</h3>
            <p>
              {search 
                ? `No groups match "${search}". Try a different search term.`
                : "No vocabulary groups are available at the moment."
              }
            </p>
          </div>
        )}

        {/* End of Results */}
        {!loading && !hasMore && groups.length > 0 && (
          <div className="end-results">
            <p>You've reached the end of the list</p>
          </div>
        )}
        </div>
      </div>
      <Footer />
    </>
  );
};

export default VocabularyGroupsPage;